import axios from 'axios';

/**
 * Extract a human-readable error message from an API error response
 * Handles FastAPI validation errors which return an array of error objects
 * @param {Error} error - The axios error object
 * @param {string} fallback - Fallback message if no specific error found
 * @returns {string} Human-readable error message
 */
export const getApiErrorMessage = (error, fallback = 'An error occurred') => {
  const detail = error?.response?.data?.detail;
  
  if (detail) {
    if (Array.isArray(detail)) {
      // Pydantic validation errors - extract the msg from each error
      return detail.map(err => err.msg || JSON.stringify(err)).join(', ');
    } else if (typeof detail === 'string') {
      return detail;
    } else if (typeof detail === 'object') {
      return JSON.stringify(detail);
    }
  }
  
  if (error?.message) {
    return error.message;
  }
  
  return fallback;
};

// Create axios instance with default config
const api = axios.create({
  baseURL: window.location.origin,
});

// Track if we're currently validating to avoid recursive calls
let isValidatingSession = false;

// Add request interceptor to include user info and session in headers
api.interceptors.request.use(
  async (config) => {
    // Get user from localStorage
    const userStr = localStorage.getItem('user');
    const sessionId = localStorage.getItem('session_id');
    
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        config.headers['X-User-Id'] = user.id || 'unknown';
        config.headers['X-User-Name'] = user.full_name || user.name || 'Unknown';
        config.headers['X-User-Email'] = user.email || 'unknown@unknown.com';
        
        // Add Authorization header with JWT token
        if (user.token) {
          config.headers['Authorization'] = `Bearer ${user.token}`;
        }
      } catch (e) {
        console.error('Failed to parse user from localStorage:', e);
      }
    }
    
    // Fallback: try to get token from separate localStorage key
    if (!config.headers['Authorization']) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    // Add session ID to all requests
    if (sessionId) {
      config.headers['X-Session-Id'] = sessionId;
      
      // For non-auth and non-health API calls, validate session first
      // Skip validation for session-related endpoints to avoid infinite loops
      const isAuthEndpoint = config.url?.includes('/auth/') || config.url?.includes('/health');
      
      if (!isAuthEndpoint && !isValidatingSession) {
        isValidatingSession = true;
        try {
          // Quick session validation before the actual request
          const response = await axios.post(`${window.location.origin}/api/auth/session/check`, {}, {
            headers: { 'X-Session-Id': sessionId }
          });
          
          if (!response.data.valid) {
            // Session is invalid - trigger logout
            isValidatingSession = false;
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            localStorage.removeItem('tokenExpiry');
            localStorage.removeItem('session_id');
            localStorage.removeItem('session_timeout');
            localStorage.removeItem('session_warning');
            
            window.dispatchEvent(new CustomEvent('session-terminated', { 
              detail: { message: response.data.message || 'Your session has been terminated.' } 
            }));
            
            // Abort the original request
            const controller = new AbortController();
            controller.abort();
            return Promise.reject(new Error('Session terminated'));
          }
        } catch (validationError) {
          // If validation fails, allow the request to proceed (might be network issue)
          console.warn('Session validation failed:', validationError.message);
        }
        isValidatingSession = false;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Normalize error detail to always be a string (prevents React render crashes)
const normalizeDetail = (detail) => {
  if (!detail) return detail;
  if (Array.isArray(detail)) {
    return detail.map(err => (typeof err === 'object' ? (err.msg || JSON.stringify(err)) : String(err))).join(', ');
  }
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
};

// Add response interceptor for error handling and session validation
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Normalize detail to string to prevent "Objects are not valid as React child" crashes
    if (error.response?.data?.detail && typeof error.response.data.detail !== 'string') {
      error.response.data.detail = normalizeDetail(error.response.data.detail);
    }

    // Handle session termination errors
    if (error.response?.status === 401) {
      const detail = error.response?.data?.detail;
      if (detail && detail.includes && (detail.includes('session') || detail.includes('terminated'))) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('tokenExpiry');
        localStorage.removeItem('session_id');
        localStorage.removeItem('session_timeout');
        localStorage.removeItem('session_warning');
        
        window.dispatchEvent(new CustomEvent('session-terminated', { 
          detail: { message: detail } 
        }));
      }
      console.error('Unauthorized request');
    }
    return Promise.reject(error);
  }
);

export default api;
